import React from 'react';

import { ComponentSize, FontSize, Orientation } from '../../../typings/design-tokens';
import { formations, teamFormation } from '../../../typings/formations';
import { placeholdersMap, PLAYERAVATAR } from '../../../utils/placeholders-football';
import Avatar from '../../atoms/avatar/avatar';
import PlayerFormationPlaceNumber from '../../atoms/player-formation-place-icon/player-formation-place-icon';
import { makeClassNames } from '../../../utils/styles';

import { IPlayerInFormation } from './match-formation';
import { getFormationSchema, IFormationPlayer, playerNameSlice } from './match-formation-field';

const EMPTY_DATA_PLACEHOLDER = '–';

export const isFormationRenderable = (formation: teamFormation, players: Array<IFormationPlayer>): boolean => {
  if (!formation || !players.length) {
    console.warn('[livedata-sports-design-system] NO FORMATION OR EMPTY PLAYER LIST'); // eslint-disable-line no-console
    return false;
  }
  return true;
};

export const isValidSchema = (formation: teamFormation, schema: IPlayerInFormation[]) => {
  const targetFormationPlaceArray = formations[formation];
  for (const elm of schema) {
    const formationPlace = elm.formationPlace;
    if (!targetFormationPlaceArray.includes(formationPlace)) {
      console.warn('[livedata-sports-design-system] INVALID FORMATION PLACE:', formationPlace); // eslint-disable-line no-console
      console.warn('[livedata-sports-design-system] VALID SCHEMA IS:', targetFormationPlaceArray); // eslint-disable-line no-console
      return false;
    }
  }
  return true;
};

export const isValidPlayerList = (formation: teamFormation, players: IFormationPlayer[]) => {
  const targetFormationPlaceArray = formations[formation];
  for (const player of players) {
    const formationPlace = player.formationPlace;
    if (!targetFormationPlaceArray.includes(formationPlace)) {
      console.warn('[livedata-sports-design-system] INVALID FORMATION PLACE FOR PLAYER:', player); // eslint-disable-line no-console
      console.warn('[livedata-sports-design-system] VALID FORMATION PLACES ARE:', targetFormationPlaceArray); // eslint-disable-line no-console
      return false;
    }
  }
  return true;
};

export const classNames = (size: ComponentSize) =>
  makeClassNames({
    MatchFormationField: `match-formation-field--${size}`,
    Player: 'match-formation-player-in-field',
    PlayersContainer: 'match-formation-players-container',
  });

export interface IFormationField {
  cn: ReturnType<typeof classNames>;
  customFormationSchema?: IPlayerInFormation[];
  formation: teamFormation;
  hasImage: boolean;
  orientation: Orientation;
  size: ComponentSize;
}

export interface IFormationFieldPlayers extends IFormationField {
  isHome?: boolean;
  players: Array<IFormationPlayer>;
}

const getPlayer = (
  hasImage: boolean,
  pName: string,
  pLabel: string,
  fontsize: FontSize | undefined,
  playerData: IFormationPlayer | undefined,
  size: ComponentSize,
  isHome: boolean | undefined
): JSX.Element => {
  return hasImage ? (
    <Avatar
      name={pName}
      label={pLabel}
      imagePosition="vertical"
      fontSizeName={fontsize}
      fontSizeLabel={fontsize}
      fontWeightName="500"
      isCircled
      colorName="light"
      colorLabel="light"
      borderThickness={0}
      imageUrl={playerData?.imageUrl ?? placeholdersMap[PLAYERAVATAR]}
    />
  ) : (
    <PlayerFormationPlaceNumber
      name={pName}
      label={pLabel}
      fontSizeName={fontsize}
      fontSizeLabel={fontsize}
      size={size}
      altColor={isHome}
    />
  );
};

export const createField = ({
  cn,
  formation,
  size,
  orientation,
  hasImage,
  customFormationSchema,
  players,
  isHome,
}: IFormationFieldPlayers) => {
  /* istanbul ignore else */ if (isFormationRenderable(formation, players) && isValidPlayerList(formation, players)) {
    const formations = getFormationSchema(orientation);
    const fontsize = size === 'small' ? '12' : '12--14';
    const formationSchema = customFormationSchema || formations[formation];

    return formationSchema
      .map((playerInfo: IPlayerInFormation) => {
        const { top, left, formationPlace, maxCharLength } = playerInfo;
        const playerData = players.find((elm) => elm.formationPlace === formationPlace);

        /* istanbul ignore else */ if (playerData) {
          const { fullName, jerseyNumber, playerPageLink } = playerData;

          const pName = fullName ? playerNameSlice(fullName, maxCharLength) : EMPTY_DATA_PLACEHOLDER;
          const pLabel = jerseyNumber ? String(jerseyNumber) : EMPTY_DATA_PLACEHOLDER;

          return (
            <div style={{ top, left }} className={cn.Player} key={formationPlace} data-formationplace={formationPlace}>
              {playerPageLink ? (
                <a href={playerPageLink} key={formationPlace}>
                  {getPlayer(hasImage, pName, pLabel, fontsize, playerData, size, isHome)}
                </a>
              ) : (
                getPlayer(hasImage, pName, pLabel, fontsize, playerData, size, isHome)
              )}
            </div>
          );
        }

        return null;
      })
      .filter((elm) => Boolean(elm));
  }
  return [];
};

/**
 * DEBUG MODE
 */
export const createFieldDEBUG = ({
  cn,
  formation = '442',
  size,
  orientation,
  hasImage,
  customFormationSchema,
}: IFormationField) => {
  const formations = getFormationSchema(orientation);
  const fontsize = size === 'small' ? '12' : '12--14';
  const formationSchema = customFormationSchema || formations[formation];
  const isValid = isValidSchema(formation, formationSchema);
  if (isValid) {
    const players = [];
    for (const player of formationSchema) {
      const { top, left, formationPlace, maxCharLength } = player;
      const Player = (
        <div
          style={{ top, left, borderWidth: 'thin', borderStyle: 'solid', borderColor: 'white' }}
          className={cn.Player}
          key={formationPlace}
        >
          {hasImage ? (
            <Avatar
              name={playerNameSlice(formationPlace, maxCharLength)}
              label={String(maxCharLength)}
              imagePosition="vertical"
              fontSizeName={fontsize}
              fontSizeLabel={fontsize}
              fontWeightName="500"
              isCircled
              colorName="light"
              colorLabel="light"
              borderThickness={0}
              imageUrl={placeholdersMap[PLAYERAVATAR]}
            />
          ) : (
            <PlayerFormationPlaceNumber
              name={playerNameSlice(formationPlace, maxCharLength)}
              label={String(maxCharLength)}
              fontSizeName={fontsize}
              fontSizeLabel={fontsize}
              size={size}
            />
          )}
        </div>
      );
      players.push(Player);
    }
    return players;
  }
  return [];
};
